/**
 * Module Metadata Types
 * 
 * Defines the complete structure for module metadata used in the
 * DBS Portal modular framework system.
 */

import { z } from 'zod'

// Enums
export enum ModuleCategory {
  IDENTITY = 'identity',
  CONTENT = 'content',
  COMMUNICATION = 'communication',
  SYSTEM = 'system',
  BUSINESS = 'business',
  ANALYTICS = 'analytics',
  INTEGRATION = 'integration'
}

export enum ModuleStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DEVELOPMENT = 'development',
  DEPRECATED = 'deprecated',
  MAINTENANCE = 'maintenance'
}

export enum RouteGuardType {
  PERMISSION = 'permission',
  ROLE = 'role',
  CUSTOM = 'custom'
}

export enum NavigationBadgeType {
  COUNT = 'count',
  STATUS = 'status',
  NEW = 'new'
}

// Route Configuration
export interface RouteGuard {
  name: string
  type: RouteGuardType
  condition: string
  redirect?: string
  message?: string
}

export interface ModuleRoute {
  path: string
  component: string
  exact?: boolean
  permissions?: string[]
  roles?: string[]
  title?: string
  description?: string
  meta?: Record<string, any>
  children?: ModuleRoute[]
  redirect?: string
  guards?: RouteGuard[]
}

// Navigation Configuration
export interface NavigationBadge {
  text: string
  color: string
  type: NavigationBadgeType
}

export interface NavigationItem {
  key: string
  label: string
  icon: string
  path: string
  category: string
  order: number
  permissions?: string[]
  roles?: string[]
  children?: NavigationItem[]
  badge?: NavigationBadge
  tooltip?: string
  external?: boolean
  target?: string
  hidden?: boolean
}

// Core Module Metadata
export interface ModuleMetadata {
  // Core Identification
  id: string
  name: string
  version: string
  description: string

  // Categorization
  category: ModuleCategory
  subcategory?: string
  tags: string[]

  // Visual Configuration
  icon: string
  color?: string
  banner?: string

  // Security and Access
  permissions: string[]
  roles?: string[]
  public?: boolean

  // Routing Configuration
  routes: ModuleRoute[]
  basePath?: string

  // Navigation Configuration
  navigation: NavigationItem[]
  hideFromNavigation?: boolean

  // Dependencies
  dependencies: string[]
  peerDependencies?: string[]
  optionalDependencies?: string[]

  // Module Status
  status: ModuleStatus
  priority: number
  experimental?: boolean

  // Metadata
  author: string
  license: string
  repository?: string
  homepage?: string
  documentation?: string

  // Feature Configuration
  features?: Record<string, boolean>
  config?: Record<string, any>

  // Lifecycle Hooks
  onLoad?: string
  onUnload?: string
  onActivate?: string
  onDeactivate?: string

  // Performance
  lazy?: boolean
  preload?: boolean
  chunkName?: string
}

// Validation Schemas using Zod
export const RouteGuardSchema = z.object({
  name: z.string(),
  type: z.nativeEnum(RouteGuardType),
  condition: z.string(),
  redirect: z.string().optional(),
  message: z.string().optional()
})

export const ModuleRouteSchema: z.ZodLazy<z.ZodObject<any>> = z.lazy(() => z.object({
  path: z.string(),
  component: z.string(),
  exact: z.boolean().optional(),
  permissions: z.array(z.string()).optional(),
  roles: z.array(z.string()).optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  meta: z.record(z.any()).optional(),
  children: z.array(ModuleRouteSchema).optional(),
  redirect: z.string().optional(),
  guards: z.array(RouteGuardSchema).optional()
}))

export const NavigationBadgeSchema = z.object({
  text: z.string(),
  color: z.string(),
  type: z.nativeEnum(NavigationBadgeType)
})

export const NavigationItemSchema: z.ZodLazy<z.ZodObject<any>> = z.lazy(() => z.object({
  key: z.string(),
  label: z.string(),
  icon: z.string(),
  path: z.string(),
  category: z.string(),
  order: z.number(),
  permissions: z.array(z.string()).optional(),
  roles: z.array(z.string()).optional(),
  children: z.array(NavigationItemSchema).optional(),
  badge: NavigationBadgeSchema.optional(),
  tooltip: z.string().optional(),
  external: z.boolean().optional(),
  target: z.string().optional(),
  hidden: z.boolean().optional()
}))

export const ModuleMetadataSchema = z.object({
  // Core Identification
  id: z.string().regex(/^[a-z0-9-]+$/, 'Module ID must be lowercase alphanumeric with hyphens'),
  name: z.string().min(1, 'Module name is required'),
  version: z.string().regex(/^\d+\.\d+\.\d+/, 'Version must follow semantic versioning'),
  description: z.string().optional(),

  // Categorization
  category: z.nativeEnum(ModuleCategory),
  subcategory: z.string().optional(),
  tags: z.array(z.string()).default([]),

  // Visual Configuration
  icon: z.string().min(1, 'Icon is required'),
  color: z.string().optional(),
  banner: z.string().optional(),

  // Security and Access
  permissions: z.array(z.string()).default([]),
  roles: z.array(z.string()).optional(),
  public: z.boolean().optional(),

  // Routing Configuration
  routes: z.array(ModuleRouteSchema).default([]),
  basePath: z.string().optional(),

  // Navigation Configuration
  navigation: z.array(NavigationItemSchema).default([]),
  hideFromNavigation: z.boolean().optional(),

  // Dependencies
  dependencies: z.array(z.string()).default([]),
  peerDependencies: z.array(z.string()).optional(),
  optionalDependencies: z.array(z.string()).optional(),

  // Module Status
  status: z.nativeEnum(ModuleStatus).default(ModuleStatus.ACTIVE),
  priority: z.number().default(100),
  experimental: z.boolean().optional(),

  // Metadata
  author: z.string().optional(),
  license: z.string().optional(),
  repository: z.string().optional(),
  homepage: z.string().optional(),
  documentation: z.string().optional(),

  // Feature Configuration
  features: z.record(z.boolean()).optional(),
  config: z.record(z.any()).optional(),

  // Lifecycle Hooks
  onLoad: z.string().optional(),
  onUnload: z.string().optional(),
  onActivate: z.string().optional(),
  onDeactivate: z.string().optional(),

  // Performance
  lazy: z.boolean().optional(),
  preload: z.boolean().optional(),
  chunkName: z.string().optional()
})

// Helper Types
export type ModuleId = string
export type ModuleName = string
export type ModuleVersion = string

// Discovery Types
export interface ModuleCandidate {
  id: string
  packageName: string
  version: string
  path: string
  packageJson: any
}

export interface PackageInfo {
  name: string
  version: string
  path: string
  packageJson: any
}

// Registry Types
export interface ModuleRegistration {
  metadata: ModuleMetadata
  timestamp: number
  source: 'package' | 'config' | 'runtime' | 'discovery'
}

export interface RegistryEvent {
  type: 'register' | 'unregister' | 'update'
  moduleId: string
  module: ModuleMetadata
  timestamp: number
}

// Validation Types
export interface ValidationError {
  field: string
  message: string
  value?: any
}

export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
  warnings?: ValidationError[]
}

// Cache Types
export interface CacheEntry<T = any> {
  value: T
  expiry: number
  timestamp: number
}

// Export validation function
export const validateModuleMetadata = (metadata: unknown): ValidationResult => {
  try {
    ModuleMetadataSchema.parse(metadata)
    return { valid: true, errors: [] }
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        valid: false,
        errors: error.errors.map(err => ({
          field: err.path.join('.'),
          message: err.message,
          value: 'received' in err ? err.received : undefined
        }))
      }
    }
    return {
      valid: false,
      errors: [{ field: 'unknown', message: 'Unknown validation error' }]
    }
  }
}
